import React, { useCallback, useEffect, useRef, useState } from 'react';
import TrainingFilterCheck from '../../components/TrainingFilterCheck';
import { riskApi } from '../../utils/riskApi';
import { Modal, SearchSelect } from '../review/reviewUi';

const MAX_TESTS = 4;
const RUN = 'T_RUN';
const MISSING_RISK = "This project has no Risk work item type (original code RISK) in Orcanos, so we can't continue. "
  + 'Add the Risk type to the project in Orcanos, or pick another project.';

/**
 * New / Edit risk panel: name, project + version, ONE Orcanos Risk filter, and up to three
 * Test Case filters for verification.
 *
 * Why several test filters: a tenant often has one filter that selects Traced Items Info and
 * another that selects Last Test Run Result (orca60 does — 742 and 817). The server merges
 * them by test key, so the user does not have to build a new filter in Orcanos first.
 *
 * A row can instead be a TEST RUN filter (item type T_RUN): a run carries its test case's Key, so
 * each test shows its runs. Orcanos cannot list Test Run filters (GetFilterList answers 400 for
 * every spelling of the type — re-verified live 2026-10-13), so the picker offers the ones the
 * account's panels already use (`/api/risk/run-filters`) and a box takes any other number.
 *
 * Each row has its OWN project and version: tests often live in a different project from the
 * risk file. Both default to the panel's; the build reads the row with its `version_id`.
 *
 * The risk filter is column-checked as soon as it is picked (Note #34): Key, Name, P and S
 * before and after, Hazard, Hazardous Situation, Risk Control and Traced Items Info. A missing
 * column is a WARNING with a "map it" picker, as in Training — it never blocks saving. A check
 * that could not RUN shows as "not checked", never as an all-clear.
 */
export default function RiskPanelForm({ panelId, onClose, onSaved }) {
  const editing = !!panelId;
  const [setup, setSetup] = useState(null);
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState('');
  const [versionId, setVersionId] = useState('');
  const [riskFilters, setRiskFilters] = useState({ available: true, filters: [], loading: false });
  const [testLists, setTestLists] = useState({});   // project_id → its Test Case filter list
  const [filterId, setFilterId] = useState('');
  const [tests, setTests] = useState([]);           // [{filter_id, filter_name, item_type, check}]
  const [runFilters, setRunFilters] = useState([]);
  // Verification source: a Risk Traceability panel (risk → mitigation → test → run), or own filters.
  const [testMode, setTestMode] = useState('trace');
  const [traceId, setTraceId] = useState('');
  const [tracePanels, setTracePanels] = useState(null);   // null = loading
  const [isPublic, setIsPublic] = useState(true);
  const [matrixP, setMatrixP] = useState(5);        // risk matrix size: P levels × S levels, 3–5 each
  const [matrixS, setMatrixS] = useState(5);
  const [mapping, setMapping] = useState({});
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const token = useRef(0);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const s = await riskApi.setup();
        if (!live) return;
        setSetup(s);
        if (editing) {
          const c = await riskApi.panelConfig(panelId);
          if (!live) return;
          setName(c.name || '');
          setProjectId(String(c.project_id || ''));
          setVersionId(String(c.version_id || ''));
          setFilterId(String(c.filter_id || ''));
          setIsPublic(!!c.is_public);
          setMatrixP(Number(c.matrix?.p) || 5);
          setMatrixS(Number(c.matrix?.s) || 5);
          setMapping(c.fields || {});
          setTests((c.tests || []).map((t) => ({ ...t, check: null })));
          setTraceId(String(c.trace_setup_id || ''));
          setTestMode(c.trace_setup_id || !(c.tests || []).length ? 'trace' : 'filters');
        } else if ((s.projects || []).filter((p) => p.has_risk !== false).length === 1) {
          const only = s.projects.find((p) => p.has_risk !== false);
          setProjectId(only.project_id);
          setVersionId(only.versions?.[0]?.version_id || '');
        }
      } catch (e) {
        if (live) setError(e.message);
      }
    })();
    return () => { live = false; };
  }, [editing, panelId]);

  const project = (setup?.projects || []).find((p) => p.project_id === projectId);
  // The project must carry the Risk work item type (original code RISK, from QW_Login).
  const noRisk = !!project && project.has_risk === false;

  // The Traceability panel list is never held over: it is re-read whenever the dropdown takes
  // focus and whenever the window comes back into view, so a level added in Traceability (often
  // in another tab, with this form still open) shows at once instead of a stale "no Test Case
  // level". A refresh keeps the current list on screen; only the first load shows "Loading".
  const liveRef = useRef(true);
  const loadTracePanels = useCallback(() => {
    riskApi.tracePanels()
      .then((d) => { if (liveRef.current) setTracePanels([...(d.linked || []), ...(d.candidates || [])]); })
      .catch(() => { if (liveRef.current) setTracePanels((prev) => prev || []); });
  }, []);

  useEffect(() => {
    liveRef.current = true;
    riskApi.runFilters().then((d) => { if (liveRef.current) setRunFilters(d.filters || []); }).catch(() => {});
    loadTracePanels();
    const onVisible = () => { if (document.visibilityState === 'visible') loadTracePanels(); };
    window.addEventListener('focus', loadTracePanels);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      liveRef.current = false;
      window.removeEventListener('focus', loadTracePanels);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadTracePanels]);

  useEffect(() => {
    if (!projectId || noRisk) return undefined;
    let live = true;
    setRiskFilters((f) => ({ ...f, loading: true }));
    riskApi.filters(projectId, 'RISK', project?.type_label || '')
      .then((d) => { if (live) setRiskFilters({ available: d.available, filters: d.filters || [], loading: false }); })
      .catch(() => { if (live) setRiskFilters({ available: false, filters: [], loading: false }); });
    return () => { live = false; };
  }, [projectId, noRisk, project?.type_label]);

  // A test row's project and version fall back to the panel's (rows saved before 4.24.1 have neither).
  const rowPid = (t) => String(t.project_id || projectId || '');
  const rowVer = (t) => String(t.version_id || versionId || '');
  const projectOf = (pid) => (setup?.projects || []).find((p) => p.project_id === pid);

  // Test Case filters, loaded once per project a row points at.
  const listPids = [...new Set(tests.filter((t) => t.item_type !== RUN).map(rowPid).filter(Boolean))].join(',');
  useEffect(() => {
    listPids.split(',').filter(Boolean).forEach((pid) => {
      if (testLists[pid]) return;
      setTestLists((m) => ({ ...m, [pid]: { available: true, filters: [], loading: true } }));
      riskApi.filters(pid, 'T_CASE', projectOf(pid)?.test_label || '')
        .then((d) => setTestLists((m) => ({ ...m, [pid]: { available: d.available, filters: d.filters || [], loading: false } })))
        .catch(() => setTestLists((m) => ({ ...m, [pid]: { available: false, filters: [], loading: false } })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listPids, setup]);
  const listFor = (t) => testLists[rowPid(t)] || { available: true, filters: [], loading: true };

  const runCheck = useCallback(async (fid, ver, map) => {
    if (!fid || !ver) { setCheck(null); return; }
    const mine = ++token.current;
    setChecking(true);
    try {
      const d = await riskApi.checkFilter({ filter_id: String(fid), version_id: String(ver), fields: map || {} });
      if (mine === token.current) setCheck(d.result);
    } catch (e) {
      if (mine === token.current) setCheck({ checked: false, reason: 'error', message: e.message });
    } finally {
      if (mine === token.current) setChecking(false);
    }
  }, []);

  useEffect(() => { runCheck(filterId, versionId, mapping); }, [filterId, versionId, mapping, runCheck]);

  // Each test filter gets its own (lighter) column check.
  const testKey = tests.map((t) => `${t.item_type || ''}:${t.filter_id}:${rowVer(t)}`).join(',');
  useEffect(() => {
    let live = true;
    tests.forEach((t, i) => {
      if (!t.filter_id || t.check || !rowVer(t)) return;
      riskApi.checkFilter({ filter_id: String(t.filter_id), version_id: rowVer(t), kind: t.item_type === RUN ? 'run' : 'test' })
        .then((d) => { if (live) setTests((xs) => xs.map((x, j) => (j === i ? { ...x, check: d.result } : x))); })
        .catch((e) => { if (live) setTests((xs) => xs.map((x, j) => (j === i ? { ...x, check: { checked: false, message: e.message } } : x))); });
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testKey, versionId]);

  const pickProject = (pid) => {
    const p = (setup?.projects || []).find((x) => x.project_id === pid);
    setProjectId(pid);
    setVersionId(p?.versions?.[0]?.version_id || '');
    setFilterId('');
    setTests([]);
    setCheck(null);
  };

  const nameOf = (list, id) => (list.filters || []).find((f) => String(f.id) === String(id))?.name || '';
  const filterName = nameOf(riskFilters, filterId);

  const testCovers = (role) => tests.some((t) => t.item_type !== RUN && t.check?.fields?.some((f) => f.role === role && f.status === 'ok'));
  const setTest = (i, patch) => setTests((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch, check: null } : x)));

  const save = async () => {
    setError('');
    if (!name.trim()) { setError('Give the panel a name.'); return; }
    if (!projectId || !versionId) { setError('Pick a project and version.'); return; }
    if (noRisk) { setError(MISSING_RISK); return; }
    if (!filterId) { setError('Pick an Orcanos risk filter.'); return; }
    setBusy(true);
    const body = {
      name: name.trim(), project_id: projectId, version_id: versionId,
      filter_id: String(filterId), filter_name: filterName, is_public: isPublic, fields: mapping,
      matrix_p: matrixP, matrix_s: matrixS,
      trace_setup_id: testMode === 'trace' ? traceId : '',
      tests: testMode === 'trace' ? [] : tests.filter((t) => t.filter_id).map((t) => ({
        filter_id: String(t.filter_id),
        filter_name: t.filter_name || (t.item_type === RUN
          ? runFilters.find((f) => String(f.filter_id) === String(t.filter_id))?.name || ''
          : nameOf(listFor(t), t.filter_id)),
        project_id: rowPid(t),
        version_id: rowVer(t),
        item_type: t.item_type === RUN ? RUN : 'T_CASE',
      })),
    };
    try {
      const res = editing ? await riskApi.updatePanel(panelId, body) : await riskApi.createPanel(body);
      onSaved(res.id || panelId);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const filterOptions = (list, current) => [
    ...(current && !(list.filters || []).some((f) => String(f.id) === String(current))
      ? [{ value: String(current), label: `#${current} (current)` }] : []),
    ...(list.filters || []).map((f) => ({ value: String(f.id), label: `${f.name} (#${f.id})` })),
  ];

  return (
    <Modal
      title={editing ? 'Edit risk panel' : 'New risk panel'}
      subtitle="A risk panel reads one Orcanos Risk filter — your risk file — and scores it with the project's FMEA settings."
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="drv-btn drv-btn--lg" onClick={onClose}>Cancel</button>
          <button type="button" className="drv-btn drv-btn--lg" onClick={() => runCheck(filterId, versionId, mapping)}
            disabled={!filterId || checking}>Check again</button>
          <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={save}
            disabled={busy || noRisk || !setup}>{busy ? 'Saving…' : 'Save panel'}</button>
        </>
      )}
    >
      {!setup && !error && <div className="drv-muted">Loading projects from Orcanos…</div>}
      <label className="drv-field">
        <span className="drv-lbl">Panel name</span>
        <input className="drv-input" value={name} onChange={(e) => { setName(e.target.value); setError(''); }}
          placeholder="e.g. Infusion Pump IP-200 — risk file" maxLength={120} autoFocus />
      </label>

      <div className="drv-row">
        <label className="drv-field">
          <span className="drv-lbl">Project</span>
          {/* Searchable; a Solution project (QW_Login Is_solution) is labelled and bold.
              The backend still returns every project, so `noRisk` below can explain a saved one. */}
          <SearchSelect
            ariaLabel="Project"
            disabled={!setup}
            value={projectId}
            onChange={(v) => { pickProject(String(v)); setError(''); }}
            placeholder={setup ? 'Search projects…' : 'Loading projects…'}
            // Only projects carrying the Risk type — plus the saved one when editing, so it still shows.
            options={(setup?.projects || []).filter((p) => p.has_risk !== false || p.project_id === projectId).map((p) => ({
              value: p.project_id,
              label: `${p.project_name}${p.is_solution ? ' (Solution)' : ''}${p.has_risk === false ? ' — no Risk type' : ''}`,
              strong: !!p.is_solution,
            }))}
          />
        </label>
        <label className="drv-field">
          <span className="drv-lbl">Version</span>
          <select className="drv-select" value={versionId} onChange={(e) => { setVersionId(e.target.value); setTests((ts) => ts.map((t) => (t.version_id ? t : { ...t, check: null }))); }}
            disabled={!project}>
            {(project?.versions || []).map((v) => <option key={v.version_id} value={v.version_id}>{v.version_label}</option>)}
          </select>
        </label>
      </div>
      {noRisk && <div className="drv-error" role="alert">{MISSING_RISK}</div>}

      {!noRisk && (<>
      <label className="drv-field">
        <span className="drv-lbl">Orcanos filter ({project?.type_label || 'Risk'})</span>
        {riskFilters.available || riskFilters.loading ? (
          <SearchSelect
            ariaLabel="Orcanos risk filter"
            disabled={!projectId || riskFilters.loading}
            value={filterId}
            onChange={(v) => { setFilterId(String(v)); setError(''); }}
            placeholder={riskFilters.loading ? 'Loading filters…' : projectId ? 'Search filters…' : 'Pick a project first'}
            options={filterOptions(riskFilters, filterId)}
          />
        ) : (
          <input className="drv-input" inputMode="numeric" placeholder="Filter ID"
            value={filterId} onChange={(e) => setFilterId(e.target.value.replace(/\D/g, ''))} />
        )}
      </label>

      <div className="drv-field">
        <span className="drv-lbl">Filter columns <span>— Key, Name, P and S before and after, Hazard, Hazardous Situation, Risk Control and Traced Items Info (finds the mitigations)</span></span>
        <TrainingFilterCheck
          result={check}
          busy={checking}
          hasFilter={!!filterId}
          mapping={mapping}
          onMap={(role, col) => setMapping((m) => {
            const next = { ...m };
            if (col) next[role] = col; else delete next[role];
            return next;
          })}
          onRecheck={() => runCheck(filterId, versionId, mapping)}
        />
        {check?.checked && !check.ok && check.message && (
          <div className="drv-note">{check.message} You can still save the panel now.</div>
        )}
      </div>

      <div className="drv-field">
        <span className="drv-lbl">Verification <span>— where test results come from</span></span>
        <div className="drv-row" role="radiogroup" aria-label="Verification source">
          <label className="drv-chk"><input type="radio" checked={testMode === 'trace'} onChange={() => setTestMode('trace')} /> From a Risk Traceability panel</label>
          <label className="drv-chk"><input type="radio" checked={testMode === 'filters'} onChange={() => setTestMode('filters')} /> My own test filters (advanced)</label>
        </div>
        {testMode === 'trace' && (
          tracePanels === null ? <div className="rsk-fine">Loading Traceability panels…</div>
            : tracePanels.length === 0 ? (
              <div className="drv-note">
                No Traceability panel starts at Risk yet. Build one in Traceability:
                Risk → mitigation (e.g. requirements) → Test Case → optional Test Run. Then pick it here.
              </div>
            ) : (
              <>
                <select className="drv-input" aria-label="Risk Traceability panel" value={traceId}
                  onFocus={loadTracePanels} onChange={(e) => setTraceId(e.target.value)}>
                  <option value="">None — find tests from Orcanos links only</option>
                  {tracePanels.map((t) => (
                    <option key={t.id} value={t.id} disabled={!t.has_tests}>
                      {t.name}{t.has_tests ? '' : ' — no Test Case level'}
                    </option>
                  ))}
                </select>
                <div className="rsk-fine">
                  Its Test Case and Test Run levels supply the results, each from its own project and version.
                  The links risk → mitigation → test are still read from Orcanos.
                </div>
              </>
            )
        )}
        {testMode === 'filters' && tests.map((t, i) => {
          const pid = rowPid(t);
          const rp = projectOf(pid);
          // Run filters the account's panels use, this row's project first. A typed number that
          // is not among them still shows as the current value.
          const runs = [...runFilters].sort((a, b) => (a.project_id === pid ? 0 : 1) - (b.project_id === pid ? 0 : 1));
          const runOptions = [
            ...(t.filter_id && !runs.some((f) => String(f.filter_id) === String(t.filter_id))
              ? [{ value: String(t.filter_id), label: `#${t.filter_id}` }] : []),
            ...runs.map((f) => ({ value: String(f.filter_id), label: `${f.name} (#${f.filter_id})` })),
          ];
          const list = listFor(t);
          return (
          // keyed by position: a key that includes the filter id remounts the row on every keystroke
          <div key={i} className="rsk-testrow">
            <select className="drv-input rsk-testkind" aria-label={`Filter ${i + 1} kind`} value={t.item_type === RUN ? RUN : 'T_CASE'}
              onChange={(e) => setTest(i, { item_type: e.target.value, filter_id: '', filter_name: '' })}>
              <option value="T_CASE">Test cases</option>
              <option value={RUN}>Test runs</option>
            </select>
            <div className="rsk-testbody">
              <div className="rsk-testline">
                <SearchSelect
                  ariaLabel={`Test filter ${i + 1} project`}
                  disabled={!setup}
                  value={pid}
                  onChange={(v) => setTest(i, {
                    project_id: String(v), version_id: projectOf(String(v))?.versions?.[0]?.version_id || '',
                    filter_id: t.item_type === RUN ? t.filter_id : '', filter_name: t.item_type === RUN ? t.filter_name : '',
                  })}
                  placeholder="Search projects…"
                  options={(setup?.projects || []).map((p) => ({
                    value: p.project_id, label: `${p.project_name}${p.is_solution ? ' (Solution)' : ''}`, strong: !!p.is_solution,
                  }))}
                />
                <select className="drv-input rsk-testver" aria-label={`Test filter ${i + 1} version`} value={rowVer(t)}
                  onChange={(e) => setTest(i, { project_id: pid, version_id: e.target.value })} disabled={!rp}>
                  {(rp?.versions || []).map((v) => <option key={v.version_id} value={v.version_id}>{v.version_label}</option>)}
                </select>
              </div>
              {t.item_type === RUN ? (
                <>
                  <div className="rsk-testline">
                    <SearchSelect
                      ariaLabel={`Test run filter ${i + 1}`}
                      value={String(t.filter_id || '')}
                      onChange={(v) => setTest(i, { filter_id: String(v), filter_name: runFilters.find((f) => String(f.filter_id) === String(v))?.name || '' })}
                      placeholder={runs.length ? 'Pick a test run filter…' : 'No panel uses one yet — type the number'}
                      emptyText="No panel uses a test run filter yet — type its number"
                      options={runOptions}
                    />
                    <input className="drv-input rsk-testnum" inputMode="numeric" aria-label={`Test run filter ${i + 1} number`}
                      placeholder="or filter #" value={String(t.filter_id || '')}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^0-9]/g, '');
                        setTest(i, { filter_id: v, filter_name: runFilters.find((f) => String(f.filter_id) === v)?.name || '' });
                      }} />
                  </div>
                  <div className="rsk-fine">
                    Orcanos can't list Test Run filters, so the list shows the ones your panels already use. For another, type its number.
                  </div>
                </>
              ) : (
                <SearchSelect
                  ariaLabel={`Test case filter ${i + 1}`}
                  disabled={list.loading}
                  value={String(t.filter_id || '')}
                  onChange={(v) => setTest(i, { filter_id: String(v), filter_name: nameOf(list, v) })}
                  placeholder={list.loading ? 'Loading filters…' : 'Search test case filters…'}
                  options={filterOptions(list, t.filter_id)}
                />
              )}
              {t.check && (
                <div className="rsk-testcheck">
                  {t.check.checked === false
                    ? <span className="rsk-dim">Not checked — {t.check.message}</span>
                    : (t.check.fields || []).map((f) => (
                      <span key={f.role} className={f.status === 'ok' ? 'ok' : 'miss'}>{f.status === 'ok' ? '✓' : '–'} {f.title}</span>
                    ))}
                </div>
              )}
            </div>
            <button type="button" className="drv-iconbtn" aria-label="Remove this test filter"
              onClick={() => setTests((xs) => xs.filter((_, j) => j !== i))}>×</button>
          </div>
          );
        })}
        {testMode === 'filters' && tests.length < MAX_TESTS && (
          <button type="button" className="drv-link" style={{ alignSelf: 'flex-start' }} disabled={!projectId}
            onClick={() => setTests((xs) => [...xs, { filter_id: '', filter_name: '', item_type: 'T_CASE', project_id: projectId, version_id: versionId, check: null }])}>
            + Add a test filter
          </button>
        )}
        {testMode === 'filters' && tests.some((t) => t.check?.checked) && !(testCovers('traced') && testCovers('result')) && (
          <div className="drv-note">
            Between them, the test filters should select <strong>Traced Items Info</strong> (to match a test to the
            requirement it verifies) and <strong>Last Test Run Result</strong>. Missing:{' '}
            {[!testCovers('traced') && 'Traced Items Info', !testCovers('result') && 'Last Test Run Result'].filter(Boolean).join(', ')}.
          </div>
        )}
      </div>

      <div className="drv-field">
        <span className="drv-lbl">Risk matrix size <span>— how many probability (P) and severity (S) levels the matrix shows; 5 × 5 by default</span></span>
        <div className="drv-row">
          <label className="drv-field">
            <span className="drv-lbl">Probability levels (P)</span>
            <select className="drv-select" aria-label="Probability levels" value={matrixP} onChange={(e) => setMatrixP(Number(e.target.value))}>
              {[3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label className="drv-field">
            <span className="drv-lbl">Severity levels (S)</span>
            <select className="drv-select" aria-label="Severity levels" value={matrixS} onChange={(e) => setMatrixS(Number(e.target.value))}>
              {[3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        </div>
        <div className="rsk-fine">A project scored 1–10 in its FMEA settings always shows 10 × 10.</div>
      </div>

      </>)}

      <div className="drv-field">
        <span className="drv-lbl">Who sees this panel</span>
        <label className="drv-chk">
          <input type="radio" name="rsk-vis" checked={isPublic} onChange={() => setIsPublic(true)} />
          <span><strong>Everyone in the account</strong></span>
        </label>
        <label className="drv-chk">
          <input type="radio" name="rsk-vis" checked={!isPublic} onChange={() => setIsPublic(false)} />
          <span><strong>Only me</strong></span>
        </label>
      </div>

      {error && <div className="drv-error" role="alert">{error}</div>}
    </Modal>
  );
}
